import React from 'react';
import { Alert, Box, Button, Grid, Paper, Tab, Tabs } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PageHeader from '../../../components/page-header';
import { formatDate } from '../../../utils/date';
import { useDashboardController } from './context/dashboard-controller';
import { useDashboardState } from './context/dashboard-context';
import { StatCard } from './components/stat-card';
import { RecentItemsList } from './components/recent-items-list';

function NewSubmissionsAlert({ count, onView }: { count?: number; onView: () => void }) {
    if (!count) {
        return <Alert severity="success">Todas as remessas de publicação estão processadas.</Alert>;
    }

    const message = count === 1
        ? 'Existe 1 remessa nova disponível para publicação.'
        : `Existem ${count} remessas novas disponíveis para publicação.`;

    return (
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={onView}>Ver agora</Button>}>
            {message}
        </Alert>
    );
}

export function DashboardComponent() {
    const ctrl = useDashboardController();
    const { dashboardDetail, officialDiaryItems, submissionPublicationItems, tabIndex } = useDashboardState();

    const recentSubmissions = submissionPublicationItems.items.map((item) => ({
        id: item.id,
        avatarName: item.userSenderName,
        title: item.title,
        highlight: item.instruction,
        caption: `Enviada em ${formatDate(item.sendDate)}`
    }));

    const recentOfficialDiaries = officialDiaryItems.items.map((item) => ({
        id: item.id,
        avatarName: item.clientName,
        title: item.clientName,
        highlight: item.description,
        caption: formatDate(item.publicationDate)
    }));

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Dashboard"
                    subtitle="Visão geral do Sistema de Diário e Publicações"
                    breadcrumbs={[{ label: 'Dashboard' }]}
                />
            </Grid>

            <Grid item xs={12}>
                <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                        <NewSubmissionsAlert count={dashboardDetail.countNewSubmissionPublication} onView={ctrl.navigateToSubmissionPublications} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <StatCard
                            label="Total de edições do diário"
                            value={dashboardDetail.countOfficialDaily}
                            caption={`${dashboardDetail.countOfficialDailyLastMonth ?? 0} no último mês`}
                            icon={<FileCopyIcon />}
                            color="warning"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <StatCard
                            label="Total de remessas"
                            value={dashboardDetail.countSubmissionPublication}
                            caption={`${dashboardDetail.countSubmissionPublicationLastMonth ?? 0} no último mês`}
                            icon={<FileUploadIcon />}
                            color="info"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <StatCard label="Remessas pendentes" value={dashboardDetail.countPendingSubmissionPublication} icon={<PendingActionsIcon />} color="error" />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <StatCard label="Remessas concluídas" value={dashboardDetail.countClosedSubmissionPublication} icon={<TaskAltIcon />} color="success" />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                    <Tabs value={tabIndex} onChange={ctrl.handleTabChange} variant="scrollable" allowScrollButtonsMobile>
                        <Tab label="Últimas remessas" />
                        <Tab label="Últimas edições do Diário Oficial" />
                    </Tabs>
                    <Box sx={{ pt: 2 }}>
                        {tabIndex === 0 && (
                            <RecentItemsList items={recentSubmissions} emptyMessage="Nenhuma remessa recebida até o momento." onSelect={ctrl.navigateToSubmissionPublicationDetail} />
                        )}
                        {tabIndex === 1 && (
                            <RecentItemsList items={recentOfficialDiaries} emptyMessage="Nenhuma edição do Diário Oficial publicada até o momento." onSelect={ctrl.navigateToOfficialDiaryDetail} />
                        )}
                    </Box>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
